import { TextField, Tooltip } from "@mui/material";
import HelpIcon from "@mui/icons-material/Help";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./css/PortInTimeRange.scss";
import { UseFormMethods, useWatch } from "react-hook-form";
import { format, isWithinInterval, parse } from "date-fns";
import { IPortinSlot } from "@features/PortIn/interface/IPortinSlot";
import { IPortinForm } from "@features/PortIn/interface/IPortinForm";

interface IParams {
    portinSlotList: IPortinSlot[];
    formMethods: UseFormMethods<IPortinForm>;
}

export const PortInTimeRange = (props: IParams) => {
    const { formMethods, portinSlotList } = props;
    const { register, errors, setError, clearErrors } = formMethods;
    const { t } = useTranslation(["portin", "validation"]);
    const [portingSlotForDate, setPortingSlotForDate] = useState<IPortinSlot>();

    const formatTime = (time: Date) => format(time, "HH:mm");
    const date = useWatch<Date>({ name: "date", control: formMethods.control });

    const checkIsInTimeRange = (newValue: string) => {
        if (portinSlotList.length) {
            if (portingSlotForDate && date) {
                const { endTime, startTime } = portingSlotForDate;
                const selectedTime = parse(newValue, "HH:mm", date);

                if (!isWithinInterval(selectedTime, { start: startTime, end: endTime })) {
                    const startTimeFormatted = formatTime(startTime);
                    const endTimeFormatted = formatTime(endTime);

                    return `Must be after ${startTimeFormatted} and before ${endTimeFormatted}`;
                }
            } else {
                return "No time available for this move date";
            }
        }

        return undefined;
    };

    useEffect(() => {
        const formattedDate = date ? format(date, "yyyy-MM-dd") : "";
        const portingSlotList = portinSlotList.find(
            (portinSlot) => format(portinSlot.date, "yyyy-MM-dd") === formattedDate
        );
        setPortingSlotForDate(portingSlotList);

        const hasTimeRange = !date || Boolean(portingSlotList);
        if (hasTimeRange) {
            clearErrors("time");
        } else {
            setError("time", { message: "No available time for the selected date" });
        }
    }, [clearErrors, date, portinSlotList, setError]);

    return (
        <Fragment>
            <TextField
                InputLabelProps={{
                    shrink: true,
                }}
                className="time-range-field"
                disabled={!portingSlotForDate}
                error={Boolean(errors.time)}
                helperText={errors.time?.message}
                id="time"
                inputProps={{
                    step: 300, // All 5 minute with arrow
                }}
                inputRef={register({
                    required: t("validation:required"),
                    validate: (value: string) => checkIsInTimeRange(value),
                })}
                label={`${t("portin:time")} *`}
                name="time"
                type="time"
                variant="standard"
            />
            <Tooltip
                color="secondary"
                disableFocusListener
                disableTouchListener
                placement="top-end"
                title={t("portin:tooltipTime")}
            >
                <HelpIcon />
            </Tooltip>
        </Fragment>
    );
};
